// Builds a standalone printable HTML report from an investigation result.
// Ported from osint-users/utils/walletReportHtml.js (nextjs_space/lib/report-html.ts).

function esc(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtNum(n: unknown, digits = 8): string {
  const v = Number(n ?? 0);
  if (isNaN(v)) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: digits });
}

function fmtDateIST(input: unknown): string {
  if (!input) return 'N/A';
  try {
    const d =
      typeof input === 'number'
        ? new Date(input < 1e12 ? input * 1000 : input)
        : new Date(input as string | number | Date);
    if (isNaN(d.getTime())) return 'N/A';
    return (
      d.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }) + ' IST'
    );
  } catch {
    return 'N/A';
  }
}

function fmtUsd(n: unknown): string {
  if (typeof n !== 'number' || isNaN(n)) return '';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function fmtInr(n: unknown): string {
  if (typeof n !== 'number' || isNaN(n)) return '';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

function fmtFiat(u: unknown, i: unknown): string {
  const us = fmtUsd(u);
  const is = fmtInr(i);
  if (us && is) return `${us} · ${is}`;
  return us || is;
}

export function buildWalletReportHtml(
  data: any,
  options: { generatedBy?: string; logoUrl?: string } = {},
): string {
  const generatedBy = String(options.generatedBy || '').trim();
  const logoUrl = esc(
    String(options.logoUrl || '/assets/images/logo.png').trim() ||
      '/assets/images/logo.png',
  );
  const profile = data?.profile ?? {};
  const symbol = data?.symbol ?? 'BTC';
  const chainName = data?.chainName ?? 'Bitcoin';
  const riskScore = data?.riskScore ?? 0;
  const riskLevel = data?.riskLevel ?? 'Unknown';
  const riskColor =
    riskScore < 30 ? '#059669' : riskScore <= 60 ? '#d97706' : '#dc2626';
  const topSenders = data?.topSenders ?? [];
  const topReceivers = data?.topReceivers ?? [];
  const transactions = (data?.transactions ?? []).slice(0, 25);
  const narrative = data?.narrative ?? '';
  const abuseCount = data?.abuseCount ?? 0;
  const abuseCategories = data?.abuseCategories ?? [];
  const communityCount = data?.communityReportCount ?? 0;
  const communityCategories = data?.communityCategories ?? [];
  const dataSources = data?.dataSources ?? [];
  const usd = data?.usd ?? {};
  const inr = data?.inr ?? {};
  const usdPrice = typeof usd?.price === 'number' ? usd.price : null;
  const inrPrice = typeof inr?.price === 'number' ? inr.price : null;
  const generatedAt = fmtDateIST(Date.now());
  const priceLine =
    usdPrice != null || inrPrice != null
      ? `1 ${esc(symbol)} = ${fmtFiat(usdPrice, inrPrice)} (as of ${generatedAt})`
      : '';
  const crossChain = data?.crossChain ?? [];
  const entityLabel = data?.entityLabel ?? null;
  const sanctions = data?.sanctions ?? { listed: false };
  const riskFactors = data?.riskFactors ?? [];
  const tokens = data?.tokens ?? [];
  const exposure = data?.exposure ?? null;
  const exposureHits = exposure?.hits ?? [];
  const threatFlags = data?.threatFlags ?? [];
  const threatOrigin = data?.threatOrigin ?? '';
  const crossChainChecked = data?.crossChainChecked ?? 0;
  const abuseSources =
    (data?.abuseSources?.length ?? 0) > 0
      ? data.abuseSources
      : [
          {
            source: 'ChainAbuse',
            status: 'ok',
            count: abuseCount,
            categories: abuseCategories,
          },
        ];
  const totalAbuse = abuseCount + communityCount + (threatFlags?.length ?? 0);
  const catStr = (arr: any[]) =>
    (arr ?? [])
      .map(
        (c) => `${esc(c?.category ?? 'Unknown')} ×${esc(c?.count ?? 1)}`,
      )
      .join(', ');

  const cpRows = (items: any[]) =>
    items
      .map(
        (cp) => `<tr>
          <td class="mono">${esc(cp?.address)}</td>
          <td class="right">${fmtNum(cp?.totalBtc)} ${esc(symbol)}</td>
          <td class="right">${esc(cp?.txCount ?? 0)}</td>
        </tr>`,
      )
      .join('');

  const txRows = transactions
    .map((tx: any) => {
      const atUsd = typeof tx?.usdAtTime === 'number' ? tx.usdAtTime : null;
      const atInr = typeof tx?.inrAtTime === 'number' ? tx.inrAtTime : null;
      const nowUsd =
        usdPrice != null ? (tx?.valueBtc ?? 0) * usdPrice : null;
      const nowInr =
        inrPrice != null ? (tx?.valueBtc ?? 0) * inrPrice : null;
      const atStr = fmtFiat(atUsd, atInr);
      const nowStr = fmtFiat(nowUsd, nowInr);
      return `<tr>
        <td class="mono small">${esc(tx?.hash ?? '')}</td>
        <td>${fmtDateIST(tx?.time)}</td>
        <td class="right ${tx?.direction === 'in' ? 'pos' : 'neg'}">
          ${tx?.direction === 'in' ? '+' : '-'}${fmtNum(tx?.valueBtc)} ${esc(symbol)}
        </td>
        <td class="right small muted">${atStr || '—'}</td>
        <td class="right small">${nowStr || '—'}</td>
      </tr>`;
    })
    .join('');

  const narrativeHtml = String(narrative)
    .split('\n\n')
    .map((p) => `<p>${esc(p)}</p>`)
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<style>
  .wallet-pdf-host, .wallet-pdf-host * { margin: 0; padding: 0; box-sizing: border-box; }
  @page { size: A4; margin: 12mm 10mm; }
  .wallet-pdf-host { height: auto; overflow: visible; font-family: 'Helvetica Neue', Arial, sans-serif; color: #111827; font-size: 14px; padding: 0; margin: 0; background: #ffffff; }
  .wallet-pdf-host .mono { font-family: 'Courier New', monospace; word-break: break-all; }
  .wallet-pdf-host .small { font-size: 12px; }
  .wallet-pdf-host .right { text-align: right; }
  .wallet-pdf-host .header { border-bottom: 3px solid #f59e0b; padding-bottom: 16px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-start; page-break-inside: avoid; }
  .wallet-pdf-host .brand-row { display: flex; align-items: center; gap: 10px; }
  .wallet-pdf-host .brand-logo { height: 32px; width: auto; display: block; object-fit: contain; }
  .wallet-pdf-host .brand { font-size: 18px; font-weight: 800; color: #111827; }
  .wallet-pdf-host .brand span { color: #d97706; }
  .wallet-pdf-host .meta { color: #4b5563; font-size: 12px; margin-top: 4px; }
  .wallet-pdf-host .risk-badge { display: inline-block; padding: 8px 18px; border-radius: 999px; color: #ffffff; font-weight: 700; font-size: 16px; background: ${riskColor}; }
  .wallet-pdf-host h2 { font-size: 16px; color: #92400e; margin: 12px 0 8px; border-bottom: 1px solid #fde68a; padding-bottom: 4px; page-break-after: avoid; }
  .wallet-pdf-host table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  .wallet-pdf-host tr, .wallet-pdf-host th, .wallet-pdf-host td { page-break-inside: avoid; }
  .wallet-pdf-host th { text-align: left; background: #fffbeb; color: #92400e; padding: 8px 10px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; }
  .wallet-pdf-host th.right { text-align: right; }
  .wallet-pdf-host td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; color: #111827; }
  .wallet-pdf-host .grid { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
  .wallet-pdf-host .kpi { flex: 1 1 22%; min-width: 130px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; page-break-inside: avoid; }
  .wallet-pdf-host .kpi .label { color: #4b5563; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
  .wallet-pdf-host .kpi .value { font-weight: 700; font-size: 16px; margin-top: 4px; color: #111827; }
  .wallet-pdf-host .abuse { border: 1px solid ${totalAbuse > 0 ? '#fecaca' : '#d1fae5'}; background: ${totalAbuse > 0 ? '#fef2f2' : '#ecfdf5'}; border-radius: 8px; padding: 12px; margin-top: 6px; font-weight: 600; font-size: 14px; color: ${totalAbuse > 0 ? '#b91c1c' : '#047857'}; page-break-inside: avoid; }
  .wallet-pdf-host .narrative p { margin-bottom: 8px; line-height: 1.65; color: #111827; font-size: 14px; }
  .wallet-pdf-host .sources { color: #4b5563; font-size: 12px; margin-top: 4px; }
  .wallet-pdf-host .footer { margin-top: 28px; padding-top: 10px; border-top: 1px solid #e5e7eb; color: #4b5563; font-size: 11px; text-align: center; line-height: 1.5; page-break-inside: avoid; }
  .wallet-pdf-host .sanction { border: 2px solid #dc2626; background: #fef2f2; border-radius: 8px; padding: 12px 14px; margin-bottom: 16px; color: #b91c1c; font-size: 14px; page-break-inside: avoid; }
  .wallet-pdf-host .sanction .t { font-weight: 800; font-size: 15px; }
  .wallet-pdf-host .label-badge { display: inline-block; background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; border-radius: 999px; padding: 2px 8px; font-size: 12px; margin-top: 4px; }
  .wallet-pdf-host .usd { color: #047857; font-size: 12px; font-weight: 600; }
  .wallet-pdf-host .factor { display: flex; justify-content: space-between; align-items: center; padding: 5px 0; border-bottom: 1px solid #f3f4f6; page-break-inside: avoid; }
  .wallet-pdf-host .factor .fl { color: #111827; font-size: 13px; }
  .wallet-pdf-host .factor .fd { color: #4b5563; font-size: 12px; }
  .wallet-pdf-host .factor .fp { font-weight: 700; font-size: 13px; }
  .wallet-pdf-host .lea { border: 1px solid #e5e7eb; border-left: 4px solid #d97706; border-radius: 8px; padding: 11px 13px; margin-bottom: 10px; page-break-inside: avoid; }
  .wallet-pdf-host .lea .lt { font-weight: 800; font-size: 14px; color: #111827; }
  .wallet-pdf-host .lea .lk { color: #4b5563; font-size: 12px; }
  .wallet-pdf-host .lea .lp { display: inline-block; border-radius: 999px; padding: 1px 8px; font-size: 11px; font-weight: 700; border: 1px solid currentColor; margin-left: 6px; }
  .wallet-pdf-host .lea dl { display: grid; grid-template-columns: 108px 1fr; gap: 2px 10px; margin: 7px 0 0; font-size: 12px; }
  .wallet-pdf-host .lea dt { color: #4b5563; }
  .wallet-pdf-host .lea dd { margin: 0; color: #111827; word-break: break-word; }
  .wallet-pdf-host .lea ul { margin: 6px 0 0 16px; padding: 0; font-size: 12px; color: #374151; }
  .wallet-pdf-host .cov { display: flex; gap: 10px; margin: 6px 0 12px; }
  .wallet-pdf-host .cov div { flex: 1; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; }
  .wallet-pdf-host .cov .cvv { font-size: 18px; font-weight: 800; color: #111827; }
  .wallet-pdf-host .cov .cvl { font-size: 11px; color: #4b5563; text-transform: uppercase; letter-spacing: .03em; }
  .wallet-pdf-host .draft { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 12px; font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; color: #111827; page-break-inside: avoid; margin-bottom: 12px; }
  .wallet-pdf-host .note { color: #4b5563; font-size: 12px; margin: 2px 0 6px; }
  .wallet-pdf-host,
  .wallet-pdf-host .pdf-page {
    background: #ffffff !important;
    color: #111827 !important;
    color-scheme: light;
  }
  .wallet-pdf-host table,
  .wallet-pdf-host thead,
  .wallet-pdf-host tbody,
  .wallet-pdf-host tr,
  .wallet-pdf-host td {
    background: #ffffff !important;
    background-color: #ffffff !important;
    color: #111827 !important;
  }
  .wallet-pdf-host th {
    background: #fffbeb !important;
    background-color: #fffbeb !important;
    color: #92400e !important;
  }
  .wallet-pdf-host .usd,
  .wallet-pdf-host td.pos,
  .wallet-pdf-host .pos { color: #059669 !important; }
  .wallet-pdf-host td.neg,
  .wallet-pdf-host .neg { color: #dc2626 !important; }
  .wallet-pdf-host .warn { color: #d97706 !important; }
  .wallet-pdf-host td.muted,
  .wallet-pdf-host .meta,
  .wallet-pdf-host .note,
  .wallet-pdf-host .sources { color: #4b5563 !important; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand-row">
        <img class="brand-logo" src="${logoUrl}" alt="Ryno" />
        <div class="brand">Crypto <span>Investigator</span></div>
      </div>
      <div class="meta">Investigation Report • ${esc(chainName)} (${esc(symbol)}) • Generated ${fmtDateIST(Date.now())}</div>
      <div class="meta mono">${esc(data?.address)}</div>
      ${priceLine ? `<div class="meta" style="color:#92400e;font-weight:600;margin-top:6px">${priceLine}</div>` : ''}
      ${entityLabel?.name ? `<div class="label-badge">${esc(entityLabel.name)} · ${esc(entityLabel.type)}</div>` : ''}
    </div>
    <div style="text-align:right">
      <div class="risk-badge">${esc(riskLevel)} Risk • ${esc(riskScore)}/100</div>
    </div>
  </div>

  ${sanctions?.listed ? `<div class="sanction">
    <div class="t">⚠ SANCTIONS / WATCHLIST MATCH</div>
    <div>Matches a known sanctioned / high-risk entity${sanctions?.entity ? `: <strong>${esc(sanctions.entity)}</strong>` : ''}${sanctions?.program ? ` (${esc(sanctions.program)})` : ''}. ${sanctions?.source ? `Source: ${esc(sanctions.source)}.` : ''}</div>
  </div>` : ''}

  <h2>Address Profile</h2>
  <p class="note">Fiat figures below are valued at the <strong>current</strong> exchange rate${priceLine ? ` (${priceLine})` : ''}. Per-transaction figures further down show both the value at the time of the transaction and the current value.</p>
  <div class="grid">
    <div class="kpi"><div class="label">Total Received</div><div class="value">${fmtNum(profile?.totalReceived)} ${esc(symbol)}</div>${usd?.totalReceived != null ? `<div class="usd">${fmtFiat(usd.totalReceived, inr?.totalReceived)}</div>` : ''}</div>
    <div class="kpi"><div class="label">Total Sent</div><div class="value">${fmtNum(profile?.totalSent)} ${esc(symbol)}</div>${usd?.totalSent != null ? `<div class="usd">${fmtFiat(usd.totalSent, inr?.totalSent)}</div>` : ''}</div>
    <div class="kpi"><div class="label">Balance</div><div class="value">${fmtNum(profile?.balance)} ${esc(symbol)}</div>${usd?.balance != null ? `<div class="usd">${fmtFiat(usd.balance, inr?.balance)}</div>` : ''}</div>
    <div class="kpi"><div class="label">Transactions</div><div class="value">${fmtNum(profile?.txCount, 0)}</div></div>
    <div class="kpi"><div class="label">First Seen</div><div class="value">${fmtDateIST(profile?.firstSeen)}</div></div>
    <div class="kpi"><div class="label">Last Seen</div><div class="value">${fmtDateIST(profile?.lastSeen)}</div></div>
    <div class="kpi"><div class="label">Address Age</div><div class="value">${esc(profile?.addressAge ?? 'Unknown')}</div></div>
    <div class="kpi"><div class="label">Abuse Reports</div><div class="value ${totalAbuse > 0 ? 'neg' : 'pos'}">${esc(totalAbuse)}</div></div>
  </div>

  ${(crossChain?.length ?? 0) > 0 ? `
  <h2>Cross-Chain Activity</h2>
  ${crossChainChecked > 0 ? `<p class="note">Screened ${esc(crossChainChecked)} networks; only chains with at least one transaction are listed.</p>` : ''}
  <table>
    <thead><tr><th>Network</th><th class="right">Transactions</th><th class="right">Token Transfers</th></tr></thead>
    <tbody>
    ${crossChain.map((c: any) => `<tr>
      <td><strong>${esc(c?.name)}</strong> <span style="color:#4b5563">(${esc(c?.symbol)})</span></td>
      <td class="right">${fmtNum(c?.txCount, 0)}</td>
      <td class="right">${fmtNum(c?.tokenTransfers, 0)}</td>
    </tr>`).join('')}
    </tbody>
  </table>` : ''}

  <h2>Risk Factor Breakdown</h2>
  ${(riskFactors ?? []).map((f: any) => `<div class="factor">
      <div><div class="fl">${esc(f?.label)}</div><div class="fd">${esc(f?.detail)}</div></div>
      <div class="fp ${(f?.points ?? 0) >= 40 ? 'neg' : (f?.points ?? 0) >= 15 ? 'warn' : 'pos'}">${(f?.points ?? 0) > 0 ? '+' + esc(f.points) : '0'} pts</div>
    </div>`).join('')}

  <h2>Abuse Check</h2>
  <div class="abuse">
    ${totalAbuse > 0
      ? `⚠ ${abuseCount} external report(s)${(threatFlags?.length ?? 0) > 0 ? ` + ${threatFlags.length} threat-intelligence flag(s)` : ''} + ${communityCount} community report(s).`
      : '✓ No abuse reports or threat-intelligence flags found for this address across all platforms.'}
  </div>
  ${(threatFlags?.length ?? 0) > 0 ? `
  <div class="abuse" style="border-color:#fecaca;background:#fef2f2;color:#b91c1c">
    Threat intelligence flags${threatOrigin ? ` (${esc(threatOrigin)})` : ''}: ${(threatFlags ?? []).map((f: any) => esc(f)).join(', ')}
  </div>` : ''}
  <table>
    <thead><tr><th>Platform</th><th>Status</th><th class="right">Findings</th><th>Categories</th></tr></thead>
    <tbody>
    ${abuseSources.map((s: any) => `<tr>
      <td><strong>${esc(s?.source)}</strong>${s?.origin ? ` <span style="color:#4b5563;font-size:12px">${esc(s.origin)}</span>` : ''}</td>
      <td>${s?.status === 'ok' ? '✓ Checked' : '-'}</td>
      <td class="right ${(s?.count ?? 0) > 0 ? 'neg' : 'pos'}" style="font-weight:600">${esc(s?.count ?? 0)}</td>
      <td>${(s?.categories?.length ?? 0) > 0 ? catStr(s.categories) : '—'}</td>
    </tr>`).join('')}
    <tr>
      <td><strong>Community (this platform)</strong></td>
      <td>✓ Checked</td>
      <td class="right ${communityCount > 0 ? 'neg' : 'pos'}" style="font-weight:600">${esc(communityCount)}</td>
      <td>${communityCategories.length ? catStr(communityCategories) : '—'}</td>
    </tr>
    </tbody>
  </table>

  ${exposure && (exposure.checkedHop1 ?? 0) > 0 ? `
  <h2>Indirect Exposure (Multi-Hop Tracing)</h2>
  ${exposureHits.length > 0
    ? `<div class="abuse" style="border-color:#fecaca;background:#fef2f2;color:#b91c1c">⚠ ${exposureHits.length} exposure hit(s) detected within ${esc(exposure.maxHops ?? 1)} hop(s) — ${exposure.checkedHop1} direct counterparties${(exposure.checkedHop2 ?? 0) > 0 ? ` and ${exposure.checkedHop2} second-hop addresses` : ''} screened.</div>
  <table>
    <thead><tr><th>Address</th><th>Hop</th><th>Reason</th><th>Entity / Program</th></tr></thead>
    <tbody>
    ${exposureHits.map((h: any) => `<tr>
      <td class="mono">${esc(h?.address)}</td>
      <td>Hop ${esc(h?.hop ?? 1)}${h?.via ? ` (via <span class="mono">${esc(String(h.via))}</span>)` : ''}</td>
      <td style="color:${h?.reason === 'sanctioned' ? '#dc2626' : h?.reason === 'mixer' ? '#7c3aed' : '#d97706'};font-weight:600">${esc(h?.reason === 'sanctioned' ? 'Sanctioned' : h?.reason === 'mixer' ? 'Mixer' : 'Community-flagged')}</td>
      <td>${esc(h?.entity ?? '')}${h?.program ? ` (${esc(h.program)})` : ''}</td>
    </tr>`).join('')}
    </tbody>
  </table>`
    : `<div class="abuse" style="border-color:#d1fae5;background:#ecfdf5;color:#047857">✓ No exposure to sanctioned, mixer, or community-flagged addresses detected within ${esc(exposure.maxHops ?? 1)} hop(s) — ${exposure.checkedHop1} direct counterparties${(exposure.checkedHop2 ?? 0) > 0 ? ` and ${exposure.checkedHop2} second-hop addresses` : ''} screened.</div>`}
  ` : ''}

  ${tokens.length > 0 ? `
  <h2>Token Holdings</h2>
  <table>
    <thead><tr><th>Token</th><th class="right">Balance</th><th class="right">USD Value</th><th>Contract</th></tr></thead>
    <tbody>
    ${tokens.map((t: any) => `<tr>
      <td><strong>${esc(t?.symbol)}</strong> <span style="color:#4b5563">${esc(t?.name ?? '')}</span></td>
      <td class="right">${fmtNum(t?.balance)}</td>
      <td class="right">${t?.usdValue != null ? fmtUsd(t.usdValue) : '—'}</td>
      <td class="mono">${esc(String(t?.contract ?? ''))}</td>
    </tr>`).join('')}
    </tbody>
  </table>` : ''}

  ${(topSenders.length > 0 || topReceivers.length > 0) ? `
  <h2>Top Counterparties</h2>
  ${topSenders.length > 0 ? `
  <table>
    <thead><tr><th>Top Senders</th><th class="right">Amount</th><th class="right">Txns</th></tr></thead>
    <tbody>${cpRows(topSenders)}</tbody>
  </table>` : ''}
  ${topReceivers.length > 0 ? `
  <table style="margin-top:10px">
    <thead><tr><th>Top Receivers</th><th class="right">Amount</th><th class="right">Txns</th></tr></thead>
    <tbody>${cpRows(topReceivers)}</tbody>
  </table>` : ''}` : ''}

  ${transactions.length > 0 ? `
  <h2>Recent Transactions (${transactions.length})</h2>
  <p class="note">"Value at Tx Time" uses the historical exchange rate on the transaction date; "Value Now" uses the current rate${priceLine ? ` (${priceLine})` : ''}. A gap between the two columns reflects price movement since the transaction.</p>
  <table>
    <thead><tr><th>Hash</th><th>Date (IST)</th><th class="right">Value</th><th class="right">Value at Tx Time</th><th class="right">Value Now</th></tr></thead>
    <tbody>${txRows}</tbody>
  </table>` : ''}

  <h2>Analyst Narrative</h2>
  <div class="narrative">${narrativeHtml}</div>

  <div class="sources">Data sources: ${esc(dataSources.join(', ') || 'N/A')}</div>

  <div class="footer">
    Generated by Ryno Crypto Investigator${generatedBy ? ` • Prepared by ${esc(generatedBy)}` : ''} • This report is informational and does not constitute legal or financial advice. • All timestamps in IST (Asia/Kolkata)
  </div>
</body>
</html>`;
}
